<script setup>
import { computed } from 'vue'
import Icon from './ui/Icon.vue'
import { fmt, hideAmounts, setFilter } from '../stores'
import { openSheet, state } from '../stores/state'
import { relativeDay, shortDay } from '../lib/dates'
import { cadenceKey } from '../lib/recurrence'
import { STATUS } from '../lib/matching'
import { t } from '../stores/i18n'

const props = defineProps({ row: { type: Object, required: true } })

const TONE = {
  good: 'bg-accent',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-goal',
  muted: 'bg-line',
}
const TEXT = {
  good: 'text-accent',
  warn: 'text-warn',
  danger: 'text-danger',
  info: 'text-goal',
  muted: 'text-faint',
}

const status = computed(() => ({
  ...STATUS[props.row.status],
  label: t(`status.${props.row.status}`),
}))
const detail = computed(() => {
  const r = props.row
  if (r.matches.length) {
    if (r.status === 'matched') return t('plan.seenOn', { day: shortDay(r.matches[0].date) })
    // Kept terse so it survives the narrow side rail; the alert feed spells it out.
    // The sign alone would give the variance away while amounts are hidden.
    const sign = hideAmounts.value ? '' : r.variance > 0 ? '+' : '−'
    return `${fmt(r.actual)} · ${sign}${fmt(Math.abs(r.variance))}`
  }
  return t('plan.due', { day: shortDay(r.due), relative: relativeDay(r.due) })
})

/** A plan is its category, so looking into one is looking at that category. */
const focusPlan = () => {
  setFilter('categories', [props.row.plan.category])
  state.ui.panel = 'transactions'
}
</script>

<template>
  <div class="group flex items-center gap-2.5 px-4 py-2.5 transition hover:bg-raised/50">
    <span class="grid size-8 shrink-0 place-items-center">
      <span class="size-2.5 rounded-full" :class="TONE[status.tone]" />
    </span>

    <button class="min-w-0 flex-1 text-left" @click="focusPlan">
      <span class="flex min-w-0 items-baseline gap-2">
        <span class="truncate text-[14px] font-medium">{{ row.plan.category }}</span>
        <span v-if="row.plan.cadence !== 'monthly'" class="shrink-0 text-[11px] text-faint">
          {{ t(cadenceKey(row.plan.cadence)) }}
        </span>
      </span>
      <span class="mt-0.5 block truncate text-[12px]" :class="TEXT[status.tone]">
        {{ detail }}
      </span>
    </button>

    <span class="tnum shrink-0 text-right">
      <!-- An automatic plan with nothing to learn from yet has no figure,
           which is not the same as a figure of nothing. -->
      <span class="block text-[14px] font-semibold" :class="row.autoBasis === 0 && 'text-faint'">
        {{ row.autoBasis === 0 ? '—' : fmt(row.expected) }}
      </span>
      <span v-if="row.autoBasis !== null" class="mt-0.5 block text-[11px] text-faint">
        {{ row.autoBasis === 0 ? t('plan.autoLearning') : t('plan.auto', { count: row.autoBasis }) }}
      </span>
    </span>

    <button
      class="btn-ghost !px-1.5 !py-1"
      :aria-label="t('common.editThing', { name: row.plan.category })"
      @click="openSheet('plan', { id: row.planId })"
    >
      <Icon name="pencil" :size="15" />
    </button>
  </div>
</template>
